import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Mic,
  Play,
  Pause,
  StopCircle,
  X,
  ChevronLeft,
  ChevronDown,
  Loader2,
  Upload,
  AlertCircle,
  ChevronRight,
  InfoIcon,
  ImageIcon,
  ClockIcon,
  CalendarIcon,
  CreditCardIcon,
  LockIcon,
  ZapIcon
} from "lucide-react";
import { useReactMediaRecorder } from "react-media-recorder";
import { speechService } from "../services/speechService";
import {
  createBackupSession,
  appendBackupChunk,
  deleteBackupSession,
  getLatestPendingBackup,
  buildWavFromSession,
  downsampleToInt16Pcm,
  BACKUP_SAMPLE_RATE,
  PendingBackup,
  BackupSessionMeta,
} from "../services/recordingBackup";
import { formatBytes } from "../utils/audioValidation";
import { ApiError } from "@/types/api";
import { useNavigate } from "react-router-dom";
import RecordingModal from "./RecordingModal";
import { Template, templateService } from "../services/templateService";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import { useRecordingAvailability } from "../hooks/useRecordingAvailability";
import PatientSelector from "../components/patients/PatientSelector";
import { Patient } from "../types/patient";
import type { SpeechOptions } from "../components/medical/interfaces";

// Extract a user-facing message from an axios/API error.
// The backend returns limit/validation errors as { detail: { message, error, ... } }
// (and sometimes { detail: "..." } or { message: "..." }), so the generic
// axios error.message ("Request failed with status code 403") must be unwrapped.
const getApiErrorMessage = (error: unknown, fallback = "Unknown error"): string => {
  const detail = (error as any)?.response?.data?.detail;
  if (detail) {
    if (typeof detail === "string") return detail;
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.error === "string") return detail.error;
  }
  const data = (error as any)?.response?.data;
  if (typeof data?.message === "string") return data.message;
  if (typeof (error as ApiError)?.message === "string") return (error as ApiError).message;
  return fallback;
};

// Update Safari detection to be more accurate
const isSafari =
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
  (navigator.userAgent.includes("Mac") &&
    navigator.userAgent.includes("Safari"));

// Add Chrome detection
const isChrome = /chrome/i.test(navigator.userAgent) && !isSafari;

export default function AudioRecorder() {
  // State declarations
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(true);
  const [showSelectionScreen, setShowSelectionScreen] = useState(false);
  const [selectedAnalysisType, setSelectedAnalysisType] = useState<"speech" | "image" | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  const [frequencyData, setFrequencyData] = useState<Uint8Array>(
    new Uint8Array(64)
  );
  const [patientName, setPatientName] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [formError, setFormError] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // New state for dropdown options
  const [options, setOptions] = useState<SpeechOptions>({
    visit_types: [],
    pronouns: [],
    note_lengths: []
  });
  const [selectedVisitType, setSelectedVisitType] = useState("");
  const [selectedPronouns, setSelectedPronouns] = useState("");
  const [selectedNoteLength, setSelectedNoteLength] = useState("");
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  // Recording availability check
  const {
    canRecord,
    isLoading: isCheckingAvailability,
    error: availabilityError,
    limitMessage,
    checkAvailability,
    handleLimitReached,
    limitType
  } = useRecordingAvailability();

  // Move RecordingLimitDebug component inside the main component
 

  const handleSubmit = () => {
    if (!selectedTemplate) {
      setFormError("Please select a template");
      return;
    }
    setFormError("");
    setShowForm(false);
    setShowSelectionScreen(true);
  };

  const handleAnalysisTypeSelect = (type: "speech" | "image") => {
    setSelectedAnalysisType(type);
    setShowSelectionScreen(false);
  };

  const handlePatientNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPatientName(e.target.value);
  };

  const handlePatientSelect = (patient: Patient | null, patientName: string) => {
    setSelectedPatient(patient);
    setPatientName(patientName);
  };

  // Ref declarations
  const [message, setMessage] = useState<{
    type: "error" | "success";
    text: string;
  } | null>(null);

  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<AudioNode | null>(null);

  // Local crash/refresh backup of the in-progress recording (IndexedDB).
  // The backup is deleted only after the upload to the backend succeeds.
  const [pendingBackup, setPendingBackup] = useState<PendingBackup | null>(null);
  const [backupAudioUrl, setBackupAudioUrl] = useState<string | null>(null);
  const backupSessionIdRef = useRef<string | null>(null);
  const pcmProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const pcmChunksRef = useRef<Int16Array[]>([]);
  const pcmSampleCountRef = useRef(0);
  const lastFlushPromiseRef = useRef<Promise<void> | null>(null);
  const isPausedRef = useRef(false);
  // Set when the user chose "Continue recording" on an interrupted session:
  // the recorder appends to that session and the upload uses its metadata.
  const continuedMetaRef = useRef<BackupSessionMeta | null>(null);
  const continueRequestRef = useRef(false);
  const timerOffsetRef = useRef(0);
  // Mirrors the current form selections so the backup session can store them
  // without adding them to startRecording's dependency list.
  const backupMetaRef = useRef<{
    patientName: string;
    patientId?: string;
    templateId?: number;
    visitType?: string;
    pronouns?: string;
    noteLength?: string;
  }>({ patientName: "" });

  // Constants
  const MAX_RECORDING_TIME = 3600; // 1 hour in seconds
  const BACKUP_CHUNK_SECONDS = 5;

  // React Media Recorder hook
  const {
    startRecording: startMediaRecording,
    stopRecording: stopMediaRecording,
    pauseRecording: pauseMediaRecording,
    resumeRecording: resumeMediaRecording,
    clearBlobUrl,
    mediaBlobUrl,
  } = useReactMediaRecorder({
    audio: true,
    onStop: (blobUrl, blob) => {
      console.log(`🎙️ Recording stopped - blob size: ${blob.size} bytes, type: ${blob.type}`);
      setAudioBlob(blob);
    },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: selectedAnalysisType === "image" 
      ? { "image/*": [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".tiff", ".dicom", ".dcm", "*"] }
      : {
          "audio/*": [".mp3", ".wav", ".m4a", ".ogg"],
          "image/*": [".jpg", ".jpeg", ".png", ".gif", ".webp"],
        },
    maxFiles: 10,
    onDrop: (acceptedFiles) => {
      // Separate audio and image files
      const audioFiles = acceptedFiles.filter(file => file.type.startsWith('audio/'));
      const imageFiles = acceptedFiles.filter(file => file.type.startsWith('image/') || file.name.endsWith('.dicom') || file.name.endsWith('.dcm'));
      
      // Handle audio file (only take the first one if multiple are uploaded)
      if (audioFiles.length > 0 && selectedAnalysisType !== "image") {
        handleFileSelect(audioFiles[0]);
      }
      
      // Handle image files
      if (imageFiles.length > 0) {
        setSelectedImages(prev => [...prev, ...imageFiles]);
      }
    },
  });

  // Keep the backup metadata in sync with the form selections
  backupMetaRef.current = {
    patientName,
    patientId: selectedPatient?.patient_id,
    templateId: selectedTemplate?.id,
    visitType: selectedVisitType || undefined,
    pronouns: selectedPronouns || undefined,
    noteLength: selectedNoteLength || undefined,
  };

  // Persist the buffered PCM samples as one IndexedDB chunk
  const flushPcmBackup = useCallback(() => {
    const sessionId = backupSessionIdRef.current;
    const chunks = pcmChunksRef.current;
    if (!sessionId || chunks.length === 0) return;
    pcmChunksRef.current = [];
    pcmSampleCountRef.current = 0;
    const total = chunks.reduce((n, c) => n + c.length, 0);
    const merged = new Int16Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    lastFlushPromiseRef.current = appendBackupChunk(
      sessionId,
      Date.now(),
      merged.buffer
    );
  }, []);

  const stopBackupCapture = useCallback(() => {
    flushPcmBackup();
    const processor = pcmProcessorRef.current;
    if (processor) {
      try {
        processor.disconnect();
      } catch (error) {
        console.warn("Recording backup: failed to disconnect capture", error);
      }
      processor.onaudioprocess = null;
      pcmProcessorRef.current = null;
    }
  }, [flushPcmBackup]);

  // Object URL so the pending backup can be listened to in the banner
  useEffect(() => {
    if (!pendingBackup) {
      setBackupAudioUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingBackup.blob);
    setBackupAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingBackup]);

  // On mount, look for a recording interrupted by a refresh/crash
  useEffect(() => {
    let cancelled = false;
    getLatestPendingBackup(backupSessionIdRef.current).then((backup) => {
      if (!cancelled && backup) {
        setPendingBackup(backup);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Warn before leaving the page while a recording is in progress
  useEffect(() => {
    if (!isRecording) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue =
        "A recording is in progress. If you leave now, it will be interrupted.";
      return event.returnValue;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isRecording]);

  // Add cleanup effect for timer
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      // Stop the backup capture on unmount; saved chunks stay in IndexedDB
      // so an interrupted recording remains recoverable.
      stopBackupCapture();
    };
  }, [stopBackupCapture]);

  // Update the handlePlayback function
  const handlePlayback = useCallback(() => {
    if (!mediaBlobUrl) return;

    try {
      // If already playing, stop playback
      if (isPlaying && audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        setIsPlaying(false);
        setTimer(0);
        return;
      }

      // Clean up any existing audio contexts and players
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current = null;
      }
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.src = "";
        audioPlayerRef.current = null;
      }

      const audio = new Audio(mediaBlobUrl);

      // Set up audio context for visualization
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      const source = audioContext.createMediaElementSource(audio);
      source.connect(analyser);
      analyser.connect(audioContext.destination);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      sourceRef.current = source;

      // Set up event listeners
      audio.addEventListener("ended", () => {
        setIsPlaying(false);
        setTimer(0);
        setFrequencyData(new Uint8Array(64));
      });

      audio.addEventListener("timeupdate", () => {
        setTimer(Math.floor(audio.currentTime));
      });

      let animationFrameId: number;
      const updateVisualization = () => {
        if (analyserRef.current && isPlaying) {
          const dataArray = new Uint8Array(
            analyserRef.current.frequencyBinCount
          );
          analyserRef.current.getByteFrequencyData(dataArray);
          setFrequencyData(dataArray);
          animationFrameId = requestAnimationFrame(updateVisualization);
        }
      };

      audio.addEventListener("play", () => {
        updateVisualization();
      });

      audio.addEventListener("pause", () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        setFrequencyData(new Uint8Array(64));
      });

      audioPlayerRef.current = audio;

      // Start playback
      audio
        .play()
        .then(() => {
          setIsPlaying(true);
          // Don't open modal for playback
          // setIsModalOpen(true);
        })
        .catch((error) => {
          console.error("Error playing audio:", error);
          setMessage({
            type: "error",
            text: "Failed to play audio",
          });
          setIsPlaying(false);
        });
    } catch (error) {
      console.error("Error in playback:", error);
      setMessage({
        type: "error",
        text: "Failed to handle audio playback",
      });
      setIsPlaying(false);
    }
  }, [mediaBlobUrl, isPlaying]);

  // Add processAudio function before stopRecording
  const processAudio = useCallback(
    async (blob: Blob | null) => {
      try {
        setIsSending(true);
        
        // Different message based on analysis type
        if (selectedAnalysisType === "image" && selectedImages.length > 0) {
          setMessage({ type: "success", text: "Processing images..." });
        } else {
          setMessage({ type: "success", text: "Processing audio..." });
        }

        // Include patient information and form data (only include optional fields if selected)
        const params = {
          patient_name: patientName,
          ...(selectedVisitType && { visit_type: selectedVisitType }),
          ...(selectedPronouns && { pronouns: selectedPronouns }),
          ...(selectedNoteLength && { note_length: selectedNoteLength }),
          ...(selectedPatient && { patient_id: selectedPatient.patient_id }),
        };

        // If only images are selected (image analysis)
        if (selectedAnalysisType === "image" && selectedImages.length > 0) {
          try {
            // Use the existing speechService for uploading images
            // This ensures we use the same authentication mechanism
            for (const imageFile of selectedImages) {
              // Convert File to Blob for consistency with the API
              const imageBlob = new Blob([imageFile], { type: imageFile.type });
              
              // Use the existing transcribeAudio method which handles auth correctly
              await speechService.transcribeAudio(
                imageBlob, 
                selectedTemplate?.id,
                {
                  ...params,
                  is_image: true,  // Flag to indicate this is an image
                  image_name: imageFile.name  // Pass the original filename
                }
              );
            }
            
            // Navigate to notes page after all images are processed
            navigate("/mynotes");
            
            setMessage({
              type: "success",
              text: "Images processed successfully.",
            });
          } catch (error) {
            console.error("Error uploading images:", error);
            throw error;
          }
          return;
        }
        
        // For speech analysis with audio
        if (blob) {
          let uploadBlob = blob;
          let uploadTemplateId = selectedTemplate?.id;
          let uploadParams: typeof params = params;
          let usedBackupSession = false;

          // When the user continued an interrupted recording, upload the
          // full audio (previous + new chunks) assembled from the local
          // backup, with the metadata captured when it originally started.
          const continuedMeta = continuedMetaRef.current;
          if (continuedMeta && backupSessionIdRef.current) {
            if (lastFlushPromiseRef.current) {
              await lastFlushPromiseRef.current;
            }
            const combined = await buildWavFromSession(
              backupSessionIdRef.current
            );
            if (combined) {
              uploadBlob = combined;
              uploadTemplateId = continuedMeta.templateId ?? uploadTemplateId;
              uploadParams = {
                patient_name: continuedMeta.patientName,
                ...(continuedMeta.visitType && { visit_type: continuedMeta.visitType }),
                ...(continuedMeta.pronouns && { pronouns: continuedMeta.pronouns }),
                ...(continuedMeta.noteLength && { note_length: continuedMeta.noteLength }),
                ...(continuedMeta.patientId && { patient_id: continuedMeta.patientId }),
              };
              usedBackupSession = true;
            }
          }

          setMessage({ type: "success", text: `Uploading audio (${formatBytes(uploadBlob.size)})...` });
          await speechService.transcribeAudio(uploadBlob, uploadTemplateId as number, uploadParams);

          // Upload succeeded — the local backup copy of this recording is no
          // longer needed. Keep it if the blob came from an uploaded file, or
          // if a continued session couldn't be assembled (only the new part
          // was uploaded, so the backup must stay recoverable).
          if (
            backupSessionIdRef.current &&
            (usedBackupSession || (!selectedFile && !continuedMeta))
          ) {
            const sessionId = backupSessionIdRef.current;
            backupSessionIdRef.current = null;
            continuedMetaRef.current = null;
            void deleteBackupSession(sessionId);
          }
        }

        navigate("/mynotes");

        setMessage({
          type: "success",
          text: "Upload completed successfully.",
        });
      } catch (error) {
        console.error("Error processing content:", error);
        setMessage({
          type: "error",
          text: getApiErrorMessage(error, "Failed to process. Please try again."),
        });
      } finally {
        setIsSending(false);
        setFrequencyData(new Uint8Array(64));
      }
    },
    [navigate, selectedTemplate, patientName, selectedPatient, selectedAnalysisType, selectedImages, selectedVisitType, selectedPronouns, selectedNoteLength, selectedFile]
  );

  // Update the stopRecording function
  const stopRecording = useCallback(async () => {
    if (!isRecording) return;

    try {
      // Request final data before stopping
      stopMediaRecording();

      // Stop the local backup capture (flushes its final chunk). The saved
      // backup itself is deleted only after the upload succeeds.
      stopBackupCapture();

      // Clear timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      // Clean up audio context
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current = null;
      }

      // Reset recording states but keep modal open
      setIsRecording(false);
      setIsPaused(false);
      setFrequencyData(new Uint8Array(64));

      // Don't upload here — the audioBlob state is stale at this point.
      // The onStop callback from useReactMediaRecorder will set the new blob,
      // and RecordingModal's auto-process useEffect will handle the upload.
    } catch (error) {
      console.error("Error stopping recording:", error);
      setMessage({
        type: "error",
        text: `Failed to stop recording: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      });
    }
  }, [
    isRecording,
    stopMediaRecording,
    stopBackupCapture,
  ]);

  // Update the startRecording function
  const startRecording = useCallback(async () => {
    try {
      // Reset error state
      setPermissionError(false);
      setMessage(null);
      
      // Check if user has reached recording limits
      const { canRecord: canStartRecording, message, redirectToPricing } = checkAvailability(0);
      
      if (!canStartRecording) {
        setMessage({
          type: "error",
          text: message || "Recording limit reached. Please upgrade your plan."
        });
        
        if (redirectToPricing) {
          // Show message before redirecting
          setTimeout(() => {
            handleLimitReached();
          }, 3000);
        }
        return;
      }
      
      setIsModalOpen(true); // Open modal before requesting permission

      // Clean up any existing audio contexts and players
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current = null;
      }
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.src = "";
        audioPlayerRef.current = null;
      }

      try {
        // Get audio stream with explicit error handling
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        // If we get here, permission was granted
        setIsRecording(true);
        setIsPaused(false);
        isPausedRef.current = false;
        // When continuing an interrupted recording, the timer starts from
        // the already-recorded duration.
        setTimer(Math.floor(timerOffsetRef.current));
        setAudioBlob(null);
        setIsPlaying(false);

        // Set up audio context and analyser
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 128;
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        sourceRef.current = source;

        // Start the local backup capture: downsampled PCM chunks are saved
        // to IndexedDB every few seconds so a refresh/crash mid-recording
        // can be recovered. When continuing an interrupted recording, new
        // chunks are appended to that same session. Failures here must
        // never block the primary recording.
        try {
          stopBackupCapture();
          const isContinuation =
            continueRequestRef.current && backupSessionIdRef.current;
          continueRequestRef.current = false;
          if (!isContinuation) {
            continuedMetaRef.current = null;
            backupSessionIdRef.current = await createBackupSession({
              ...backupMetaRef.current,
              sampleRate: BACKUP_SAMPLE_RATE,
            });
          }
          if (backupSessionIdRef.current) {
            pcmChunksRef.current = [];
            pcmSampleCountRef.current = 0;
            const captureRate = audioContext.sampleRate;
            const processor = audioContext.createScriptProcessor(4096, 1, 1);
            processor.onaudioprocess = (event) => {
              if (isPausedRef.current) return;
              const input = event.inputBuffer.getChannelData(0);
              const pcm = downsampleToInt16Pcm(input, captureRate);
              pcmChunksRef.current.push(pcm);
              pcmSampleCountRef.current += pcm.length;
              if (
                pcmSampleCountRef.current >=
                BACKUP_SAMPLE_RATE * BACKUP_CHUNK_SECONDS
              ) {
                flushPcmBackup();
              }
            };
            source.connect(processor);
            // Required for onaudioprocess to fire; output stays silent so
            // nothing is played back through the speakers.
            processor.connect(audioContext.destination);
            pcmProcessorRef.current = processor;
          }
        } catch (error) {
          console.warn("Recording backup: failed to start backup capture", error);
        }

        // Start media recording
        await startMediaRecording();

        // Start timer
        const startTime = Date.now() - timerOffsetRef.current * 1000;
        timerOffsetRef.current = 0;
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
        timerIntervalRef.current = setInterval(() => {
          const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
          setTimer(elapsedSeconds);
        }, 100);

        // Start visualization - use ref check instead of state to avoid stale closures
        let animationFrameId: number;
        const updateFrequencyData = () => {
          if (analyserRef.current) {
            const dataArray = new Uint8Array(
              analyserRef.current.frequencyBinCount
            );
            analyserRef.current.getByteFrequencyData(dataArray);
            setFrequencyData(dataArray);
            animationFrameId = requestAnimationFrame(updateFrequencyData);
          }
        };
        updateFrequencyData();

        return () => {
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
          }
        };
      } catch (error) {
        // Handle specific permission errors
        if (error instanceof DOMException) {
          if (
            error.name === "NotAllowedError" ||
            error.name === "PermissionDeniedError"
          ) {
            setPermissionError(true);
            setMessage({
              type: "error",
              text: "Microphone access was denied. Please allow microphone access in your browser settings to record audio.",
            });
          } else if (error.name === "NotFoundError") {
            setMessage({
              type: "error",
              text: "No microphone was found. Please ensure a microphone is connected and try again.",
            });
          } else {
            setMessage({
              type: "error",
              text: `Recording error: ${error.message}`,
            });
          }
          throw error;
        }
      }
    } catch (error) {
      console.error("Error starting recording:", error);
      // Keep modal open if there's a permission error
      if (!permissionError) {
        setIsModalOpen(false);
      }
      setIsRecording(false);
      setTimer(0);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  }, [
    startMediaRecording,
    checkAvailability,
    handleLimitReached,
    stopBackupCapture,
    flushPcmBackup
  ]);

  // Continue an interrupted recording from where it stopped: the recorder
  // appends new chunks to the same backup session, and stopping uploads the
  // combined audio through the normal flow.
  const continueRecording = useCallback(() => {
    if (!pendingBackup) return;

    const { meta, approxSeconds } = pendingBackup;
    continuedMetaRef.current = meta;
    continueRequestRef.current = true;
    backupSessionIdRef.current = meta.id;
    timerOffsetRef.current = approxSeconds;

    // Restore the visit context and jump straight to the recording screen
    setPatientName(meta.patientName);
    setSelectedAnalysisType("speech");
    setShowForm(false);
    setShowSelectionScreen(false);
    setPendingBackup(null);

    startRecording();
  }, [pendingBackup, startRecording]);

  const handlePauseResume = useCallback(() => {
    if (!isRecording) return;

    const newPausedState = !isPaused;
    setIsPaused(newPausedState);

    if (newPausedState) {
      pauseMediaRecording();
      isPausedRef.current = true;
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    } else {
      resumeMediaRecording();
      isPausedRef.current = false;
      // Start timer from current value
      const startTime = Date.now() - timer * 1000;
      timerIntervalRef.current = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
        setTimer(elapsedSeconds);

        if (elapsedSeconds >= MAX_RECORDING_TIME) {
          stopRecording();
        }
      }, 100);
    }
  }, [
    isRecording,
    isPaused,
    timer,
    MAX_RECORDING_TIME,
    pauseMediaRecording,
    resumeMediaRecording,
    stopRecording,
  ]);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setAudioBlob(file);
    setIsModalOpen(true);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const handleNewRecording = () => {
    setAudioBlob(null);
    setSelectedFile(null);
    // The previous recording was intentionally discarded — drop its backup
    if (backupSessionIdRef.current) {
      void deleteBackupSession(backupSessionIdRef.current);
      backupSessionIdRef.current = null;
    }
    continuedMetaRef.current = null;
    setTimer(0);
    setIsModalOpen(false);
    setIsRecording(false);
    setIsPaused(false);

    // Clear any existing intervals
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Clear any existing audio player
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.src = "";
      audioPlayerRef.current = null;
    }
  };

  // Remove the frequency animation effect since we're using real audio data now
  useEffect(() => {
    if (!isRecording && !isPlaying) {
      setFrequencyData(new Uint8Array(64));
    }
  }, [isRecording, isPlaying]);

  // Add retryRecording function
  const retryRecording = useCallback(async () => {
    // Reset error state and start recording again
    setPermissionError(false);
    startRecording();
  }, [startRecording]);

  // Update Safari settings helper to show website-specific instructions
  const handleDeleteRecording = useCallback(
    (recordingId: string) => {
      setAudioBlob(null);
      setTimer(0);
      setSelectedFile(null);
      // The recording was intentionally deleted — drop its local backup too
      if (backupSessionIdRef.current) {
        void deleteBackupSession(backupSessionIdRef.current);
        backupSessionIdRef.current = null;
      }
      continuedMetaRef.current = null;
      clearBlobUrl();
      setIsModalOpen(false);

      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.src = "";
        audioPlayerRef.current = null;
      }

      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current = null;
      }
      setFrequencyData(new Uint8Array(64));

      setMessage({
        type: "success",
        text: "Recording deleted successfully",
      });

      setTimeout(() => {
        setMessage(null);
      }, 3000);
    },
    [clearBlobUrl]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".relative")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoadingTemplates(true);
        const fetchedTemplates = await templateService.getTemplates();
        setTemplates(fetchedTemplates);
        // Remove default template selection
        setSelectedTemplate(null);
      } catch (error) {
        console.error("Error fetching templates:", error);
      } finally {
        setIsLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, []);

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
    setIsDropdownOpen(false);
  };

  // Template dropdown component
  const TemplateDropdown = () => (
    <div className="relative">
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="w-full px-4 py-2.5 text-left text-sm bg-white border border-[#E5E5EA] rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:border-blue-500 flex items-center justify-between"
      >
        <span className="truncate">
          {selectedTemplate ? selectedTemplate.name : "Select Template"}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${
            isDropdownOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isDropdownOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-[#E5E5EA] rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {isLoadingTemplates ? (
            <div className="px-4 py-2 text-sm text-gray-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading templates...
            </div>
          ) : templates.length === 0 ? (
            <div className="px-4 py-2 text-sm text-gray-500">
              No templates available
            </div>
          ) : (
            templates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                className="w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors flex items-center justify-between group"
              >
                <div>
                  <span className="font-medium text-gray-900">
                    {template.name}
                  </span>
                  {template.description && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {template.description}
                    </p>
                  )}
                </div>
                {template.is_default && (
                  <span className="text-xs font-medium text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    Default
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );

  // Fetch dropdown options on component mount
  useEffect(() => {
    const fetchOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const fetchedOptions = await speechService.getOptions();
        
        // Use API options if available, otherwise use hardcoded defaults
        const finalOptions = {
          visit_types: fetchedOptions.visit_types.length > 0 ? fetchedOptions.visit_types : [
            { value: "New Patient", label: "New Patient" },
            { value: "Returning Patient", label: "Returning Patient" },
            { value: "Unknown", label: "Unknown" }
          ],
          pronouns: fetchedOptions.pronouns.length > 0 ? fetchedOptions.pronouns : [
            { value: "He/Him", label: "He/Him" },
            { value: "She/Her", label: "She/Her" },
            { value: "They/Them", label: "They/Them" }
          ],
          note_lengths: fetchedOptions.note_lengths.length > 0 ? fetchedOptions.note_lengths : [
            { value: "Concise", label: "Concise - Key findings only" },
            { value: "Standard", label: "Standard - Normal detail level" },
            { value: "Detailed", label: "Detailed - Comprehensive notes" }
          ]
        };
        
        setOptions(finalOptions);
        
        // Optional fields - no default values needed since they are optional
      } catch (error) {
        console.error("Error fetching options:", error);
        
        // Fallback to hardcoded options if API fails
        const fallbackOptions = {
          visit_types: [
            { value: "New Patient", label: "New Patient" },
            { value: "Returning Patient", label: "Returning Patient" },
            { value: "Unknown", label: "Unknown" }
          ],
          pronouns: [
            { value: "He/Him", label: "He/Him" },
            { value: "She/Her", label: "She/Her" },
            { value: "They/Them", label: "They/Them" }
          ],
          note_lengths: [
            { value: "Concise", label: "Concise - Key findings only" },
            { value: "Standard", label: "Standard - Normal detail level" },
            { value: "Detailed", label: "Detailed - Comprehensive notes" }
          ]
        };
        
        setOptions(fallbackOptions);
        
        // Optional fields - no default values needed since they are optional
      } finally {
        setIsLoadingOptions(false);
      }
    };

    fetchOptions();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {pendingBackup && !isModalOpen && (
        <div className="container mx-auto px-6 pt-6 max-w-xl">
          <div className="relative bg-amber-50 border border-amber-200 rounded-2xl p-5 shadow-sm">
            <button
              onClick={() => setPendingBackup(null)}
              title="Hide (the recording stays saved on this device)"
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-amber-100 transition-colors"
            >
              <X className="w-4 h-4 text-[#86868B]" />
            </button>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#1D1D1F]">
                  Unfinished recording found
                </p>
                <p className="text-sm text-[#86868B] mt-1">
                  A recording
                  {pendingBackup.meta.patientName
                    ? ` for ${pendingBackup.meta.patientName}`
                    : ""}{" "}
                  ({formatTime(pendingBackup.approxSeconds)} min) was
                  interrupted. Listen to it below, then continue recording
                  from where it stopped — everything will be uploaded together
                  when you finish.
                </p>
                {backupAudioUrl && (
                  <audio
                    controls
                    src={backupAudioUrl}
                    className="w-full mt-3"
                  />
                )}
                <button
                  onClick={continueRecording}
                  className="flex items-center gap-2 mt-3 px-4 py-2 bg-[#0071E3] text-white text-sm rounded-lg font-medium hover:bg-[#0077ED] transition-all"
                >
                  <Mic className="w-4 h-4" />
                  Continue recording
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showForm ? (
        <div className="container mx-auto px-6 pt-20 max-w-xl">
          <div className="space-y-6 text-center mb-10">
            <h3 className="text-4xl font-semibold text-[#1D1D1F] tracking-tight">
              New Visit
            </h3>
            <p className="text-[#86868B] text-lg">
              Create a new medical note with voice recording
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
            <div className="space-y-8">
              {/* Patient - Full Width */}
              <div>
                <label
                  className="block text-sm font-medium text-[#1D1D1F] mb-2"
                >
                  Patient{" "}
                  <span className="text-[#86868B]">(optional)</span>
                </label>
                <div className="[&_.ant-select]:w-full [&_.ant-select_.ant-select-selector]:!px-4 [&_.ant-select_.ant-select-selector]:!py-3 [&_.ant-select_.ant-select-selector]:!border-gray-200 [&_.ant-select_.ant-select-selector]:!rounded-xl [&_.ant-select_.ant-select-selector]:!shadow-sm [&_.ant-select_.ant-select-selector]:hover:!border-gray-300 [&_.ant-select-focused_.ant-select-selector]:!border-[#0071E3] [&_.ant-select-focused_.ant-select-selector]:!shadow-[0_0_0_2px_rgba(0,113,227,0.2)]">
                  <PatientSelector
                    selectedPatientId={selectedPatient?.id}
                    selectedPatientName={patientName}
                    onPatientSelect={handlePatientSelect}
                    placeholder="Select a patient from the list"
                    size="large"
                  />
                </div>
              </div>

              {/* Template - Full Width */}
              <div>
                <label
                  htmlFor="noteType"
                  className="block text-sm font-medium text-[#1D1D1F] mb-2"
                >
                  Template <span className="text-[#FF3B30]">*</span>
                </label>
                <TemplateDropdown />
              </div>

              {/* Optional Fields - 2 Column Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Visit Type Dropdown */}
                <div>
                  <label
                    className="block text-sm font-medium text-[#1D1D1F] mb-2"
                  >
                    Visit Type{" "}
                    <span className="text-[#86868B]">(optional)</span>
                  </label>
                  <select
                    value={selectedVisitType}
                    onChange={(e) => setSelectedVisitType(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm hover:border-gray-300 focus:border-[#0071E3] focus:ring-2 focus:ring-[#0071E3]/20 focus:outline-none transition-all"
                    disabled={isLoadingOptions}
                  >
                    <option value="">Select visit type</option>
                    {options.visit_types.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Pronouns Dropdown */}
                <div>
                  <label
                    className="block text-sm font-medium text-[#1D1D1F] mb-2"
                  >
                    Pronouns{" "}
                    <span className="text-[#86868B]">(optional)</span>
                  </label>
                  <select
                    value={selectedPronouns}
                    onChange={(e) => setSelectedPronouns(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm hover:border-gray-300 focus:border-[#0071E3] focus:ring-2 focus:ring-[#0071E3]/20 focus:outline-none transition-all"
                    disabled={isLoadingOptions}
                  >
                    <option value="">Select pronouns</option>
                    {options.pronouns.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Note Length Dropdown - Full Width on 2nd Row */}
                <div className="md:col-span-2">
                  <label
                    className="block text-sm font-medium text-[#1D1D1F] mb-2"
                  >
                    Note Length{" "}
                    <span className="text-[#86868B]">(optional)</span>
                  </label>
                  <select
                    value={selectedNoteLength}
                    onChange={(e) => setSelectedNoteLength(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm hover:border-gray-300 focus:border-[#0071E3] focus:ring-2 focus:ring-[#0071E3]/20 focus:outline-none transition-all"
                    disabled={isLoadingOptions}
                  >
                    <option value="">Select note length</option>
                    {options.note_lengths.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {formError && (
              <div className="mt-4 py-2 px-4 bg-[#FF3B30]/5 rounded-xl border border-[#FF3B30]/10">
                <p className="text-[#FF3B30] text-sm text-center">
                  {formError}
                </p>
              </div>
            )}

            <button
              onClick={handleSubmit}
              className="mt-8 w-full px-6 py-4 bg-gradient-to-r from-[#0071E3] to-[#40A9FF] text-white text-lg rounded-xl font-medium hover:from-[#0077ED] hover:to-[#40A9FF] transition-all active:scale-[0.98] shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!selectedTemplate || isLoadingOptions}
            >
              Continue
            </button>
          </div>
        </div>
      ) : showSelectionScreen ? (
        <div className="container mx-auto px-6 pt-20 max-w-xl">
          <div className="space-y-6 text-center mb-10">
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 text-[#1D1D1F] hover:text-[#0071E3] transition-colors mb-4"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <h3 className="text-4xl font-semibold text-[#1D1D1F] tracking-tight">
              Choose Analysis Type
            </h3>
            <p className="text-[#86868B] text-lg">
              Select how you want to create your medical note
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button
              onClick={() => handleAnalysisTypeSelect("speech")}
              className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 transition-all hover:shadow-xl hover:border-blue-100 flex flex-col items-center text-center group"
            >
              <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                <Mic className="w-12 h-12 text-[#0071E3]" />
              </div>
              <h4 className="text-xl font-semibold text-[#1D1D1F] mb-2">
                Speech Analysis
              </h4>
              <p className="text-[#86868B]">
                Record or upload an audio conversation
              </p>
            </button>

            <button
              onClick={() => handleAnalysisTypeSelect("image")}
              className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 transition-all hover:shadow-xl hover:border-blue-100 flex flex-col items-center text-center group"
            >
              <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                <ImageIcon className="w-12 h-12 text-[#0071E3]" />
              </div>
              <h4 className="text-xl font-semibold text-[#1D1D1F] mb-2">
                Image Analysis
              </h4>
              <p className="text-[#86868B]">
                Upload and analyze medical images
              </p>
            </button>
          </div>
        </div>
      ) : (
        <div className="container mx-auto px-6 py-12">
          <div className="flex flex-col gap-2 mb-8">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSelectionScreen(true)}
                className="p-2 hover:bg-gray-50 rounded-full transition-all"
              >
                <ChevronLeft className="w-5 h-5 text-[#1D1D1F]" />
              </button>
              <h2 className="text-2xl font-semibold text-[#1D1D1F]">
                New Visit
              </h2>
            </div>
            
            <div className="mt-2 ml-9 bg-gray-50 px-5 py-4 rounded-xl border border-gray-100">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-[#86868B]">Patient</p>
                  <p className="text-base font-medium text-[#1D1D1F]">
                    {selectedPatient ? (
                      <span>
                        {selectedPatient.name}
                        {selectedPatient.medical_record_number && (
                          <span className="text-sm text-[#86868B] ml-1">
                            (MRN: {selectedPatient.medical_record_number})
                          </span>
                        )}
                      </span>
                    ) : patientName ? (
                      patientName
                    ) : (
                      "Not specified"
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#86868B]">Template</p>
                  <p className="text-base font-medium text-[#1D1D1F]">
                    {selectedTemplate?.name || "Not selected"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#86868B]">Visit Type</p>
                  <p className="text-base font-medium text-[#1D1D1F]">
                    {selectedVisitType || <span className="text-[#86868B]">Optional</span>}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#86868B]">Pronouns</p>
                  <p className="text-base font-medium text-[#1D1D1F]">
                    {selectedPronouns || <span className="text-[#86868B]">Optional</span>}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#86868B]">Note Length</p>
                  <p className="text-base font-medium text-[#1D1D1F]">
                    {selectedNoteLength || <span className="text-[#86868B]">Optional</span>}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#86868B]">Analysis Type</p>
                  <p className="text-base font-medium text-[#1D1D1F]">
                    {selectedAnalysisType === "speech" ? "Speech" : "Image"}
                  </p>
                </div>
              </div>
            </div>
          </div>

       

          {/* Enhanced Limit UI with different treatments based on limitType */}
          {(!canRecord && limitMessage) ? (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`bg-white rounded-lg shadow-sm border overflow-hidden mb-6 ${
                (limitType === 'trial_expired')
                  ? 'border-amber-300' 
                  : (limitType === 'monthly_limit')
                    ? 'border-blue-200' 
                    : (limitType === 'subscription_error')
                      ? 'border-red-200'
                      : 'border-gray-200'
              }`}
            >
              <div className={`p-4 border-b flex items-center justify-between ${
                (limitType === 'trial_expired')
                  ? 'bg-amber-50 border-amber-100' 
                  : (limitType === 'monthly_limit')
                    ? 'bg-[#f0f6ff] border-blue-100' 
                    : (limitType === 'subscription_error')
                      ? 'bg-red-50 border-red-100'
                      : 'bg-[#f0f6ff] border-gray-100'
              }`}>
                <h3 className={`text-base font-semibold flex items-center ${
                  (limitType === 'trial_expired')
                    ? 'text-amber-700' 
                    : (limitType === 'monthly_limit')
                      ? 'text-[#0078d4]' 
                      : (limitType === 'subscription_error')
                        ? 'text-red-600'
                        : 'text-[#0078d4]'
                }`}>
                  {(limitType === 'trial_expired') ? (
                    <CalendarIcon className="h-5 w-5 mr-2" />
                  ) : (limitType === 'monthly_limit') ? (
                    <InfoIcon className="h-5 w-5 mr-2" />
                  ) : (limitType === 'subscription_error') ? (
                    <CreditCardIcon className="h-5 w-5 mr-2" />
                  ) : (
                    <InfoIcon className="h-5 w-5 mr-2" />
                  )}
                  {(limitType === 'trial_expired')
                    ? 'Trial Period Expired' 
                    : (limitType === 'monthly_limit')
                      ? 'Monthly Limit Reached' 
                      : (limitType === 'subscription_error')
                        ? 'Subscription Issue'
                        : 'Recording Limit Reached'}
                </h3>
                
                {(limitType === 'trial_expired') && (
                  <motion.div 
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ repeat: Infinity, repeatType: "reverse", duration: 1 }}
                    className="bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-1 rounded-full"
                  >
                    Upgrade Now
                  </motion.div>
                )}
              </div>
              
              <div className="p-6 flex flex-col items-center text-center">
                <motion.div 
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
                    (limitType === 'trial_expired')
                      ? 'bg-amber-50' 
                      : (limitType === 'monthly_limit')
                        ? 'bg-[#f0f6ff]' 
                        : (limitType === 'subscription_error')
                          ? 'bg-red-50'
                          : 'bg-[#f0f6ff]'
                  }`}
                >
                  {(limitType === 'trial_expired') ? (
                    <ClockIcon className="h-10 w-10 text-amber-500" />
                  ) : (limitType === 'monthly_limit') ? (
                    <AlertCircle className="h-10 w-10 text-[#0078d4]" />
                  ) : (limitType === 'subscription_error') ? (
                    <CreditCardIcon className="h-10 w-10 text-red-500" />
                  ) : (
                    <LockIcon className="h-10 w-10 text-[#0078d4]" />
                  )}
                </motion.div>
                
                <motion.h4 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                  className={`text-xl font-semibold mb-2 ${
                    (limitType === 'trial_expired')
                      ? 'text-amber-800' 
                      : (limitType === 'monthly_limit')
                        ? 'text-[#323130]' 
                        : (limitType === 'subscription_error')
                          ? 'text-red-700'
                          : 'text-[#323130]'
                  }`}
                >
                  {(limitType === 'trial_expired')
                    ? 'Your Free Trial Has Expired' 
                    : (limitType === 'monthly_limit')
                      ? 'Monthly Recording Limit Reached' 
                      : (limitType === 'subscription_error')
                        ? 'Subscription Payment Issue'
                        : 'Recording Limit Reached'}
                </motion.h4>
                
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className="text-[#605e5c] max-w-lg mb-5"
                >
                  {limitMessage || 'You have reached your recording limit.'} 
                  {(limitType === 'trial_expired')
                    ? ' Subscribe now to unlock unlimited recordings and premium features.'
                    : (limitType === 'monthly_limit')
                      ? ' Upgrade to our premium plan for more recordings per month.'
                      : (limitType === 'subscription_error')
                        ? ' Please update your payment information to continue.'
                        : ' Upgrade to a premium plan for more recordings and additional features.'
                  }
                </motion.p>
                
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                >
                  <button
                    onClick={handleLimitReached}
                    className={`px-5 py-3 text-white text-sm rounded-lg font-medium transition-all flex items-center shadow-md ${
                      (limitType === 'trial_expired')
                        ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-amber-200/50' 
                        : (limitType === 'monthly_limit')
                          ? 'bg-gradient-to-r from-[#0078d4] to-[#106ebe] hover:from-[#106ebe] hover:to-[#005a9e] shadow-blue-200/50' 
                          : (limitType === 'subscription_error')
                            ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-red-200/50'
                            : 'bg-gradient-to-r from-[#0078d4] to-[#106ebe] hover:from-[#106ebe] hover:to-[#005a9e] shadow-blue-200/50'
                    }`}
                  >
                    <ZapIcon className="mr-2 h-4 w-4" />
                    <span>
                      {(limitType === 'trial_expired')
                        ? 'Subscribe Now' 
                        : (limitType === 'monthly_limit')
                          ? 'Upgrade Plan' 
                          : (limitType === 'subscription_error')
                            ? 'Update Payment'
                            : 'Upgrade Now'
                      }
                    </span>
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </button>
                </motion.div>
              </div>
            </motion.div>
          ) : null}

          <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${!canRecord && limitMessage ? 'opacity-50 pointer-events-none' : ''}`}>
            {/* Recording Section - show conditionally based on selected analysis type */}
            {selectedAnalysisType === "speech" && (
              <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-[#1D1D1F]">
                    Capture Conversation
                  </h3>
                </div>

                <div className="p-8 flex flex-col items-center justify-center min-h-[400px] relative bg-gradient-to-b from-gray-50/50 to-white">
                  {!isRecording ? (
                    <div className="text-center relative flex flex-col items-center">
                      <motion.div
                        onClick={canRecord ? startRecording : handleLimitReached}
                        className={`group relative flex flex-col items-center cursor-pointer ${!canRecord ? 'opacity-50' : ''}`}
                        whileHover={{ scale: canRecord ? 1.02 : 1 }}
                        whileTap={{ scale: canRecord ? 0.98 : 1 }}
                      >
                        <div className="text-center">
                          <div className={`w-32 h-32 bg-gradient-to-b from-gray-50 to-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-gray-100 ${isCheckingAvailability ? 'animate-pulse' : ''}`}>
                            {isCheckingAvailability ? (
                              <Loader2 className="w-12 h-12 text-[#0071E3] animate-spin" />
                            ) : (
                              <Mic className="w-12 h-12 text-[#0071E3]" />
                            )}
                          </div>
                          <h4 className="text-xl font-semibold text-[#1D1D1F] mb-2">
                            {isCheckingAvailability 
                              ? "Checking Availability..." 
                              : canRecord 
                                ? "Click To Capture Conversation" 
                                : "Recording Limit Reached"}
                          </h4>
                          <p className="text-[#86868B]">
                            {canRecord 
                              ? "Tap the microphone to begin" 
                              : "Please upgrade your plan to continue"}
                          </p>
                        </div>
                      </motion.div>
                    </div>
                  ) : (
                    <div className="space-y-8 w-full">
                      <div className="flex justify-center">
                        <div className="relative">
                          {/* Multiple pulsing rings */}
                          <div className="absolute -inset-8">
                            <div className="w-full h-full rounded-full bg-[#FF3B30]/5 animate-ping" />
                          </div>
                          <div className="absolute -inset-6">
                            <div className="w-full h-full rounded-full bg-[#FF3B30]/10 animate-ping animation-delay-300" />
                          </div>
                          <div className="absolute -inset-4">
                            <div className="w-full h-full rounded-full bg-[#FF3B30]/15 animate-ping animation-delay-600" />
                          </div>

                          {/* Main recording button */}
                          <div className="w-24 h-24 rounded-full bg-gradient-to-r from-[#FF3B30] to-[#FF453A] p-[2px] shadow-lg shadow-red-500/30">
                            <div className="w-full h-full rounded-full bg-[#FF3B30] flex items-center justify-center relative overflow-hidden">
                              <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent" />
                              <div className="w-8 h-8 rounded-sm bg-white relative z-10" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Control buttons with enhanced styling */}
                      <div className="flex justify-center gap-4">
                        <motion.button
                          onClick={handlePauseResume}
                          className="px-6 py-3 bg-white rounded-xl font-medium text-[#1D1D1F] hover:bg-gray-50 transition-all shadow-sm border border-gray-200 flex items-center gap-2"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {isPaused ? (
                            <Play className="w-4 h-4" />
                          ) : (
                            <Pause className="w-4 h-4" />
                          )}
                          {isPaused ? "Resume" : "Pause"}
                        </motion.button>
                        <motion.button
                          onClick={stopRecording}
                          className="px-6 py-3 bg-gradient-to-r from-[#FF3B30] to-[#FF453A] text-white rounded-xl font-medium hover:from-[#FF453A] hover:to-[#FF453A] transition-all shadow-lg shadow-red-500/20 flex items-center gap-2"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <StopCircle className="w-4 h-4" />
                          Stop Recording
                        </motion.button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Upload Section - Available for both speech and image analysis */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-[#1D1D1F]">
                  Upload {selectedAnalysisType === "speech" ? "Files" : "Images"}
                </h3>
              </div>

              <div
                {...getRootProps()}
                className={`p-8 flex flex-col items-center justify-center min-h-[400px] relative bg-gradient-to-b from-gray-50/50 to-white ${
                  isDragActive ? "bg-[#0071E3]/5" : "hover:bg-gray-50"
                } transition-all cursor-pointer`}
              >
                <input {...getInputProps()} disabled={isModalOpen} />
                <div className="text-center">
                  <div className="w-32 h-32 bg-gradient-to-b from-gray-50 to-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-gray-100">
                    <Upload className="w-12 h-12 text-[#0071E3]" />
                  </div>
                  <h4 className="text-xl font-semibold text-[#1D1D1F] mb-2">
                    {isDragActive ? "Drop the files here" : "Drag & Drop"}
                  </h4>
                  <p className="text-[#86868B] mb-4">
                    or click to browse your files
                  </p>
                  <p className="text-xs text-[#86868B]">
                    {selectedAnalysisType === "speech" ? (
                      <>Audio: MP3, WAV, M4A, OGG<br />Images: JPG, PNG, GIF</>
                    ) : (
                      <>All image formats supported</>
                    )}
                  </p>
                </div>
              </div>

              {/* Display uploaded images */}
              {selectedImages.length > 0 && (
                <div className="p-6 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-[#1D1D1F] flex items-center">
                      <ImageIcon className="w-4 h-4 mr-2 text-[#0071E3]" />
                      Uploaded Images ({selectedImages.length})
                    </h3>
                    {selectedImages.length > 0 && (
                      <button
                        onClick={() => setSelectedImages([])}
                        className="text-xs text-[#FF3B30] hover:text-[#FF453A] transition-colors"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {selectedImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <div className="h-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`Preview ${index}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newImages = [...selectedImages];
                            newImages.splice(index, 1);
                            setSelectedImages(newImages);
                          }}
                          className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3 text-[#1D1D1F]" />
                        </button>
                        <p className="text-xs truncate mt-1 text-[#86868B]">
                          {image.name}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Surface upload/processing errors (e.g. recording limit reached) */}
                  {message?.type === "error" && (
                    <div className="mt-6 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                      {message.text}
                    </div>
                  )}

                  {/* Show process button for image analysis */}
                  {selectedAnalysisType === "image" && (
                    <button
                      onClick={() => processAudio(null)}
                      disabled={isSending}
                      className="w-full mt-6 px-4 py-3 bg-gradient-to-r from-[#0071E3] to-[#40A9FF] text-white rounded-xl font-medium hover:from-[#0077ED] hover:to-[#40A9FF] transition-all active:scale-[0.98] shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isSending ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Process Images
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <RecordingModal
        isModalOpen={isModalOpen}
        permissionError={permissionError}
        isSafari={isSafari}
        isChrome={isChrome}
        onClose={() => setIsModalOpen(false)}
        patientName={patientName}
        timer={timer}
        frequencyData={frequencyData}
        isRecording={isRecording}
        isPaused={isPaused}
        isSending={isSending}
        isPlaying={isPlaying}
        audioBlob={audioBlob}
        formatTime={formatTime}
        retryRecording={retryRecording}
        handleNewRecording={handleNewRecording}
        handlePauseResume={handlePauseResume}
        stopRecording={stopRecording}
        handlePlayback={handlePlayback}
        processAudio={processAudio}
      />
    </div>
  );
}
